import { memo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import ArrowDown from '@/assets/ic_arrow_down.svg';
import ArrowUp from '@/assets/ic_arrow_up.svg';
import BallGreen from '@/assets/ic_ball_green.svg';
import BallOrange from '@/assets/ic_ball_orange.svg';
import { Button } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import ROUTES from '@/router/routes';
import { cn, formatNumberToLocale } from '@/utils';

const PositionChange = memo(function PositionChange({
  label,
  position,
  change,
  noCenter = false,
}: {
  label: string;
  position: string;
  change: string;
  noCenter?: boolean;
}) {
  const isPositive = Number(change) > 0;
  const isMobile = useMediaQuery({ query: '(max-width: 425px)' });

  const formatTextSize = (text: string) =>
    text.length > (isMobile ? 15 : 20) ? text.slice(0, 15) + '..' : text;

  const canShowTooltip = (text: string, maxLength: number) => {
    return text && text.length > maxLength;
  };
  const formatColor = (() => {
    if (change === '0' || change === '-') return 'text-gdt-gray-2';
    return !isPositive ? 'text-gdt-status-green' : 'text-gdt-status-red';
  })();

  return (
    <div className="text-left">
      <p
        className="font-semibold text-gdt-gray-2 text-xs whitespace-nowrap uppercase"
        id={`${label.replaceAll(' ', '')}-gral`}
      >
        {formatTextSize(label)}
      </p>
      {canShowTooltip(label, isMobile ? 15 : 20) && (
        <Tooltip
          anchorSelect={`#${label.replaceAll(' ', '')}-gral`}
          content={label}
        />
      )}
      <span
        className={cn('font-bold text-sm text-gdt-gray ', {
          'text-center': !noCenter,
        })}
      >
        {position}
      </span>
      <div className={cn('flex w-full', { 'justify-start': !noCenter })}>
        {change === '0' || change === '-' ? null : (
          <img
            alt="ic Arrow"
            className="w-5 h-5 mr-1 self-center inline-block align-middle"
            src={!isPositive ? ArrowUp : ArrowDown}
          />
        )}
        <span className={`rounded-full font-semibold text-sm ${formatColor}`}>
          {change !== '0' && change !== '-'
            ? change.replaceAll('-', '')
            : change}
        </span>
      </div>
    </div>
  );
});

export const GeneralTournament = memo(function GeneralTournamentMemo() {
  const { userInitialData } = useAuth();
  const { isMobile } = useIsDesktop();
  const navigate = useNavigate();
  let texts = {
    generalPosition: '-',
    generalChange: '-',

    statePosition: '-',
    stateChange: '-',
    countryChange: '-',
    countryPosition: '-',
    hoodPosition: '-',
    hoodChange: '-',
    points: '-',
  };

  if (userInitialData?.fecha_publicada) {
    texts.points = formatNumberToLocale(userInitialData.puntos_general ?? 0);
    texts.generalChange = formatNumberToLocale(
      (userInitialData.resultadoCambioPosicionGral === 1 ? -1 : 1) *
        (userInitialData.diferenciaPosicionGral ?? 0)
    );
    texts.generalPosition = formatNumberToLocale(
      userInitialData.posicion_general ?? 0
    );
    texts.statePosition = formatNumberToLocale(
      userInitialData?.infoRankingProvincia?.posicionGeneral ?? 0
    );
    texts.countryPosition = formatNumberToLocale(
      userInitialData?.rankingLocalidad?.posicionGeneral ?? 0
    );
    texts.stateChange = formatNumberToLocale(
      userInitialData.infoRankingProvincia.posicionGeneralAnterior
        ? userInitialData.infoRankingProvincia.posicionGeneral -
            userInitialData.infoRankingProvincia.posicionGeneralAnterior
        : 0
    );

    texts.hoodPosition = formatNumberToLocale(
      userInitialData.rankingLocalidad?.posicionGeneral ?? 0
    );
    texts.hoodChange = formatNumberToLocale(
      (userInitialData.resultadoCambioPosicionLocalidadGral === 1 ? -1 : 1) *
        (userInitialData.diferenciaPosicionLocalidadGral ?? 0)
    );

    texts.countryChange = formatNumberToLocale(
      (userInitialData.resultadoCambioPosicionLocalidadGral === 1 ? -1 : 1) *
        (userInitialData.diferenciaPosicionLocalidadGral ?? 0)
    );
  }

  texts = {
    ...texts,
    points: texts.points === '0' ? '-' : texts.points,
    generalPosition:
      texts.generalPosition === '0' ? '-' : texts.generalPosition,
    statePosition: texts.statePosition === '0' ? '-' : texts.statePosition,
    hoodPosition: texts.hoodPosition === '0' ? '-' : texts.hoodPosition,
    hoodChange: texts.hoodChange === '0' ? '-' : texts.hoodChange,
    generalChange: texts.generalChange === '0' ? '-' : texts.generalChange,
    stateChange: texts.stateChange === '0' ? '-' : texts.stateChange,
    countryChange: texts.countryChange === '0' ? '-' : texts.countryChange,
    countryPosition:
      texts.countryPosition === '0' ? '-' : texts.countryPosition,
  };

  const goToCompleteData = () => {
    navigate(ROUTES.editAccount.myData);
  };

  return (
    <div className="flex flex-col w-full gap-y-4 h-full">
      <div className="flex gap-x-3 items-center">
        <div className="p-3 bg-gdt-status-green-light md:bg-gdt-status-orange-light rounded-full">
          <img
            alt="ic Calendar"
            className="w-4"
            src={isMobile ? BallGreen : BallOrange}
          />
        </div>
        <span className="font-bold text-zinc-900 text-lg">Torneo General</span>
      </div>
      <div className="flex flex-col h-full">
        <div className="py-10 px-4 lg:px-8 bg-white rounded-t-md w-full border-b border-gdt-gray-6">
          <div className="flex gap-x-3 items-start justify-between">
            <div className="flex gap-3 items-start flex-col md:flex-row">
              <span className="text-sm text-gdt-gray-3">MIS PUNTOS</span>
              <span className="text-4xl text-gdt-gray-2">{texts.points}</span>
            </div>
            {!userInitialData?.primera_fecha && (
              <Button
                className="font-bold text-sm justify-self-center md:mr-6"
                href={ROUTES.ranking}
                type="link"
                variant={isMobile ? 'secondary' : 'text'}
              >
                Ver ranking
              </Button>
            )}
          </div>
        </div>

        <div className="py-3 bg-white rounded-b-md w-full flex-1">
          <div className="flex flex-col gap-y-5">
            <div className="flex gap-x-3 items-center justify-between">
              <p className="text-sm text-zinc-900 font-bold px-4 lg:px-8">
                Ranking
              </p>
              {!userInitialData?.barrio && !userInitialData?.dtExtranjero && (
                <span className="text-2xs font-semibold text-gdt-gray-3">
                  Para conocer el de tu Provincia y Barrio,{' '}
                  <button
                    className="text-gdt-blue underline"
                    onClick={goToCompleteData}
                  >
                    completá tus datos.
                  </button>
                </span>
              )}
            </div>
            <div className="flex justify-evenly">
              <div className="flex flex-col gap-y-2 pl-4 lg:pl-8 md:border-r md:border-gdt-gray-6 flex-1 lg:max-w-[140px] max-w-[80px]">
                <PositionChange
                  change={texts.generalChange}
                  label="GENERAL"
                  noCenter={true}
                  position={texts.generalPosition}
                />
              </div>
              <div className=" flex flex-col gap-y-2 md:border-r items-center flex-1 lg:min-w-[140px] min-w-[125px]">
                {userInitialData?.dtExtranjero ? (
                  <PositionChange
                    change={texts.countryChange}
                    label={
                      userInitialData.rankingLocalidad?.nombreLocalidad ??
                      'PROVINCIA'
                    }
                    position={texts.countryPosition}
                  />
                ) : (
                  <PositionChange
                    change={texts.stateChange}
                    label={userInitialData?.provincia ?? 'PROVINCIA'}
                    position={texts.statePosition}
                  />
                )}
              </div>
              <div className="flex flex-col gap-y-2 items-center flex-1 lg:min-w-[150px] min-w-[125px]">
                {!userInitialData?.dtExtranjero && (
                  <PositionChange
                    change={texts.hoodChange}
                    label={userInitialData?.barrio ?? 'BARRIO'}
                    position={texts.hoodPosition}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
